import {useBulkPromotion} from '@/hooks/promotion/useBulkPromotion'
import {useCSVFileValidation} from '@/hooks/useCSVFileValidation-hook'
import {useFileUpload} from '@/hooks/useFileUpload'
import {useSuggestedSKU} from '@/hooks/useSuggestedSKU.jsx'
import {INVENTORY_UPLOAD_HISTORY_KEY} from '@/hooks/useUploadHistory'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {useQueryClient} from '@tanstack/react-query'
import {useState} from 'react'

const formatFileSize = bytes => {
  if (!bytes) return undefined
  const kb = bytes / 1024
  return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`
}

const normalizeFeedbackMessage = message =>
  Array.isArray(message) ? message.join(' ') : message || 'Upload failed. Please try again.'

/**
 * Extracted logic for the v2 FileUploadWithModal: a single-file uploader that defers the
 * actual upload to one of three known flows (picked via `uploadKind`), instead of the old
 * pattern of matching on the modal's `title` text to decide which imperative ref method to call.
 * - `template`: generic CSV template upload (product list / inventory templates).
 * - `suggestedSku`: suggested SKU CSV upload.
 * - `bulkPromotion`: bulk promotion upload (2-step: upload+validate, then confirm/process).
 */
export const useFileUploadWithModal = ({setOpenUpload, uploadFor, uploadKind = 'template', toastMessage}) => {
  const [file, setFile] = useState(null)
  const [totalCount, setTotalCount] = useState(0)
  const [fileFeedback, setFileFeedback] = useState(null)
  const [uploading, setUploading] = useState(false)

  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const {validateCSV} = useCSVFileValidation()
  const {getFileUploadDetails} = useFileUpload()
  const {handleBulkPromotionFile} = useBulkPromotion(file)
  const {uploadSuggestedSKUFile} = useSuggestedSKU()

  // Only the template/suggestedSku flows validate CSV columns client-side before enabling Done -
  // bulk promotions are validated server-side as part of the upload itself.
  const requiresCsvValidation = uploadKind !== 'bulkPromotion'

  const setFeedback = (message, feedbackType = 'error') => {
    setFileFeedback({feedbackType, helperText: normalizeFeedbackMessage(message)})
    // Clear the file on error so it disappears instead of sitting there marked red -
    // matches the template flow, where CSV validation errors happen before a file is ever set.
    if ((uploadKind === 'bulkPromotion' || uploadKind === 'suggestedSku') && feedbackType === 'error') {
      setFile(null)
    }
  }

  const isCloseModal = () => {
    setOpenUpload(false)
  }

  const handleFileSelect = selectedFile => {
    setFileFeedback(null)

    if (!selectedFile) return

    if (!requiresCsvValidation) {
      setFile(selectedFile)
      return
    }

    validateCSV(selectedFile, uploadFor, ({data, errors}) => {
      if (errors.length > 0) {
        setFeedback(errors)
        return
      }

      setTotalCount(data?.length ?? 0)
      setFile(selectedFile)
    })
  }

  const handleFileDelete = () => {
    setFile(null)
    setFileFeedback(null)
  }

  const handleFileError = () => {
    setFeedback('Invalid file. Please check the accepted file type and try again.')
  }

  const uploadTemplateFile = async () => {
    await getFileUploadDetails({
      file,
      uploadFor,
      totalCount,
      onSuccess: () => {
        dispatch(setToastNotifications({show: true, message: toastMessage, type: 'success'}))
        isCloseModal()
        queryClient.invalidateQueries({queryKey: [INVENTORY_UPLOAD_HISTORY_KEY]})
      },
      onError: setFeedback,
    })
  }

  const handleDone = async () => {
    setUploading(true)
    try {
      if (uploadKind === 'bulkPromotion') {
        await handleBulkPromotionFile(isCloseModal, setUploading, setFeedback)
      } else if (uploadKind === 'suggestedSku') {
        await uploadSuggestedSKUFile(isCloseModal, setUploading, setFeedback, file)
      } else {
        await uploadTemplateFile()
      }
    } finally {
      setUploading(false)
    }
  }

  const hasError = fileFeedback?.feedbackType === 'error'

  const files = file
    ? [
        {
          name: file.name,
          size: formatFileSize(file.size),
          hasError,
          status: uploading ? 'uploading' : 'completed',
        },
      ]
    : []

  return {
    files,
    fileFeedback,
    hasError,
    uploading,
    handleFileSelect,
    handleFileDelete,
    handleFileError,
    handleDone,
    isCloseModal,
  }
}
